import type { Dimensions, XYPosition } from 'reactflow';
import type { ElkNode } from 'elkjs/lib/elk-api';

import { BUTTON_ICON_SIZE } from '@ricoh/ria.client.core/constants/component/button';
import { LAYOUT_SPACER } from '@ricoh/ria.client.core/constants/ui/layout';
import { ClientError } from '@ricoh/ria.client.core/errors';
import ELK from 'elkjs/lib/elk.bundled.js';

import type {
  ProcessFlowEdge,
  ProcessFlowLayout,
  ProcessFlowLayoutDirection,
  ProcessFlowNode,
  ProcessFlowNodeType,
} from '~/common/model/api/batch-type/process-flow';

import { PROCESS_FLOW_NODE_DISTANCE, PROCESS_FLOW_NODE_DOT_SIZE, PROCESS_FLOW_NODE_STEP_SIZE } from '~/common/constants/feature/settings/batch-type-library';

import type { NodeRect, ParentNodeRect } from './_layout.type';

import { getInboundEdges, getOutboundEdges } from './_edge';
import { getChildNodes, getFlowNodes, getParentNodes } from './_node';

/**
 * Beauty offset of node.
 *
 * @param {ProcessFlowNodeType | undefined} type Node type.
 * @param {XYPosition} offset Offset.
 *
 * @returns {XYPosition} Beautiful offset.
 */
export function beautifyOffset(type: ProcessFlowNodeType | undefined, offset: XYPosition = { x: 0, y: 0 }): XYPosition {
  switch (type) {
    case 'start':
    case 'end': {
      return {
        x: -PROCESS_FLOW_NODE_DOT_SIZE / 2 + offset.x,
        y: -PROCESS_FLOW_NODE_DOT_SIZE / 2 + offset.y,
      };
    }

    case 'add': {
      return {
        x: -BUTTON_ICON_SIZE.SMALL / 2 + offset.x,
        y: -BUTTON_ICON_SIZE.SMALL / 2 + offset.y,
      };
    }

    default: {
      return {
        x: 0 + offset.x,
        y: 0 + offset.y,
      };
    }
  }
}

/**
 * Normalize NodeRect.
 *
 * @param {ProcessFlowNode | undefined} node Node.
 *
 * @returns {NodeRect} Normalized NodeRect.
 */
function normalizeNodeRect(node: ProcessFlowNode | undefined): NodeRect {
  return {
    x: node?.position.x ?? 0,
    y: node?.position.y ?? 0,
    width: node?.width ?? 0,
    height: node?.height ?? 0,
    offset: beautifyOffset(node?.type),
  };
}

/**
 * Get ParentNode rect.
 *
 * @param {ProcessFlowNode[]} nodes Nodes.
 *
 * @returns {ParentNodeRect} ParentNode rect.
 */
export function calculateAnchor(nodes: ProcessFlowNode[]): ParentNodeRect {
  let left: ProcessFlowNode | undefined;
  let right: ProcessFlowNode | undefined;
  let bottom: ProcessFlowNode | undefined;

  nodes.forEach((node) => {
    if (!left || node.position.x < left.position.x) {
      left = node;
    }

    if (!right || node.position.x > right.position.x) {
      right = node;
    }

    if (!bottom || node.position.y > bottom.position.y) {
      bottom = node;
    }
  });

  return {
    left: normalizeNodeRect(left),
    right: normalizeNodeRect(right),
    bottom: normalizeNodeRect(bottom),
  };
}

/**
 * Layout added Nodes.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {ProcessFlowNode[]} addedNodes Added Nodes.
 *
 * @returns {ProcessFlowLayout} Layout.
 */
export function layoutAddedNodes(layout: ProcessFlowLayout, addedNodes: ProcessFlowNode[]): ProcessFlowLayout {
  if (!addedNodes.length) {
    return layout;
  }

  const conditionStepNode = addedNodes.find((node) => node.type === 'step' && node.data.type === 'condition');
  const addNodes = getChildNodes(layout, conditionStepNode?.id ?? '').nodes;

  // * Condition layout
  if (conditionStepNode && addNodes.length > 1) {
    const [convergentAddNode] = getChildNodes(layout, addNodes[0]?.id ?? '').nodes;
    if (!convergentAddNode) {
      throw new ClientError({
        module: 'logic',
        message: 'Invalid layout, not found convergent AddNode',
        cause: { layout, addedNodes },
      });
    }

    const conditionStepNodeSize = {
      width: conditionStepNode.width ?? 0,
      height: conditionStepNode.height ?? 0,
    };

    const parentNodes = getParentNodes(layout, conditionStepNode.id);
    const parentAnchor = calculateAnchor(parentNodes);
    const middleX = Math.abs(parentAnchor.right.x + parentAnchor.right.width - parentAnchor.left.x) / 2;

    // * Layout condition node
    conditionStepNode.position.x = parentAnchor.left.x + middleX - conditionStepNodeSize.width / 2;
    conditionStepNode.position.y = parentAnchor.bottom.y + parentAnchor.bottom.height + PROCESS_FLOW_NODE_DISTANCE + parentAnchor.bottom.offset.y;

    // * Layout add nodes
    const addNodeSize = {
      width: convergentAddNode.width ?? 0,
      height: convergentAddNode.height ?? 0,
    };
    const addNodeOffset = beautifyOffset('add');
    const leftAddNodeStartX =
      conditionStepNode.position.x + (conditionStepNodeSize.width - (addNodes.length - 1) * (addNodeSize.width + PROCESS_FLOW_NODE_DISTANCE * 2)) / 2;

    addNodes.forEach((addNode, index) => {
      if (index === 0) {
        addNode.position.x = leftAddNodeStartX + addNodeOffset.x;
      } else {
        const leftAddNodeX = addNodes[index - 1]?.position.x ?? leftAddNodeStartX;
        const leftAddNodeWidth = addNodes[index - 1]?.width ?? 0;

        addNode.position.x = leftAddNodeX + leftAddNodeWidth + PROCESS_FLOW_NODE_DISTANCE * 2;
      }

      const offsetY = addNodes.length % 2 === 1 && index === Math.floor(addNodes.length / 2) ? PROCESS_FLOW_NODE_DISTANCE * 1.5 : PROCESS_FLOW_NODE_DISTANCE;
      addNode.position.y = conditionStepNode.position.y + conditionStepNodeSize.height + offsetY + addNodeOffset.y;
    });

    // * Layout convergent add node
    convergentAddNode.position.x = conditionStepNode.position.x + conditionStepNodeSize.width / 2 + addNodeOffset.x;
    convergentAddNode.position.y = (addNodes[0]?.position.y ?? 0) + PROCESS_FLOW_NODE_DISTANCE;

    // * Layout connected child nodes
    const childNodes = getChildNodes(layout, convergentAddNode.id).nodes;
    const childNodesPositionY = Math.max(...childNodes.map((node) => node.position.y));
    const offset = convergentAddNode.position.y - childNodesPositionY + PROCESS_FLOW_NODE_DISTANCE / 2;
    getFlowNodes(layout, convergentAddNode.id).forEach((node) => {
      node.position.y += offset;

      return node;
    });
  }
  // * Normal layout
  else {
    addedNodes.forEach((addedNode) => {
      const parentNodes = getParentNodes(layout, addedNode.id);
      const parentAnchor = calculateAnchor(parentNodes);
      const middleX = Math.abs(parentAnchor.right.x + parentAnchor.right.width - parentAnchor.left.x) / 2;

      let distance = PROCESS_FLOW_NODE_DISTANCE;
      if (parentNodes.length === 1 && parentNodes[0]?.type === 'add') {
        distance = PROCESS_FLOW_NODE_DISTANCE * 0.5;
      }

      // * Layout added node
      addedNode.position.x = parentAnchor.left.x + middleX - (addedNode.width ?? 0) / 2;
      addedNode.position.y = parentAnchor.bottom.y + parentAnchor.bottom.height + distance + parentAnchor.bottom.offset.y;

      // * Layout connected child nodes
      const childNodes = getChildNodes(layout, addedNode.id).nodes;
      const childNodesPositionY = Math.max(...childNodes.map((node) => node.position.y));
      const offset = addedNode.position.y - childNodesPositionY + PROCESS_FLOW_NODE_DISTANCE * 1.5;
      getFlowNodes(layout, addedNode.id).forEach((node) => {
        node.position.y += offset;

        return node;
      });
    });
  }

  return layout;
}

/**
 * Calculate node step size.
 *
 * @param {string} label Label.
 *
 * @returns {Dimensions} Node step size.
 */
export function calculateNodeStepSize(label: string): Dimensions {
  const FONT_SIZE = 13.6;

  const width = LAYOUT_SPACER.PX[3] * 2 + label.length * FONT_SIZE; // padding * 2 + text length * font size

  const row = width / PROCESS_FLOW_NODE_STEP_SIZE.MAX_WIDTH;
  const floorRow = Math.floor(row);
  const height = LAYOUT_SPACER.PX[3] * 2 + (row > floorRow ? floorRow + 1 : row) * FONT_SIZE; // padding * 2 row * font size

  return {
    width: Math.min(Math.max(PROCESS_FLOW_NODE_STEP_SIZE.MIN_WIDTH, width), PROCESS_FLOW_NODE_STEP_SIZE.MAX_WIDTH),
    height: Math.max(PROCESS_FLOW_NODE_STEP_SIZE.MIN_HEIGHT, height),
  };
}

/**
 * Normalize Layout.
 *
 * @param {ProcessFlowLayout} data ProcessFlow Layout.
 *
 * @returns {ProcessFlowLayout} Normalized ProcessFlow Layout.
 */
export function normalizeLayout(data: ProcessFlowLayout): ProcessFlowLayout {
  const nodes: ProcessFlowNode[] = [];
  const edges: ProcessFlowEdge[] = [];

  // * Logic of code
  // Remove ADD_NODE and outbound of ADD_NODE
  // Old: [NODE1] --default--> [ADD_NODE] --default--> [NODE2]
  // New: [NODE1] --default--> [NODE2]
  data.nodes.forEach((node) => {
    if (node.type !== 'add') {
      nodes.push(node);
      return;
    }

    const inboundEdges = getInboundEdges(data, node.id);
    const outboundEdges = getOutboundEdges(data, node.id);

    const [inboundEdge] = inboundEdges;
    const [outboundEdge] = outboundEdges;
    if (!(inboundEdges.length === 1 && inboundEdge?.type === 'default' && outboundEdges.length === 1 && outboundEdge?.type === 'default')) {
      nodes.push(node);
      return;
    }

    data.edges.forEach((edge) => {
      // * Remove the outbound edge from 'add'
      if (edge.id === outboundEdge.id) {
        return;
      }

      // * Make edge connect to the node after 'add' to keep flow intact
      if (edge.id === inboundEdge.id) {
        edge.target = outboundEdge.target;

        // * Forward rootId and keep breakable
        if (edge.type === 'default') {
          edge.data.breakable = outboundEdge.data.breakable;
          edge.data.rootId = inboundEdge.data.rootId;
        }

        // * keep add label
        // edge.data.hideAddLabel = true;
      }

      edges.push(edge);
    });
  });

  return {
    nodes,
    edges,
  };
}

/**
 * Get Rearrange Layout.
 *
 * @param {ProcessFlowLayout} layout Layout.
 * @param {ProcessFlowLayoutDirection} direction Direction.
 *
 * @returns {Promise<ProcessFlowLayout>} ProcessFlowLayout.
 */
export async function getRearrangeLayout({ nodes, edges }: ProcessFlowLayout, direction: ProcessFlowLayoutDirection = 'DOWN'): Promise<ProcessFlowLayout> {
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,
      'elk.edgeRouting': 'POLYLINE',
      'elk.spacing.nodeNode': '500',
      'elk.spacing.edgeNode': '200',
      'elk.layered.spacing.nodeNodeBetweenLayers': '250',
    },
    children: nodes.map((node) => ({
      'id': node.id,
      'elk.position': {
        x: node.position?.y,
        y: node.position?.x,
      },
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const elk = new ELK();
  const elkLayout = await elk.layout(graph);
  if (!elkLayout.children) {
    return {
      nodes: [],
      edges: [],
    };
  }

  return {
    nodes: elkLayout.children.map((node) => {
      const initialNode = nodes.find((n) => n.id === node.id);
      if (!initialNode) {
        throw new Error('Invalid layout, node not found');
      }

      return {
        ...initialNode,
        position: {
          x: (() => {
            if (node.x) {
              if (initialNode.type === 'step') {
                return node.x - 83;
              }

              if (initialNode.type === 'start' || initialNode.type === 'end') {
                return node.x + 7;
              }
            }

            return node.x;
          })(),
          y: node.y,
        },
        sourcePosition: 'bottom',
        targetPosition: 'top',
      };
    }),
    edges: (elkLayout.edges ?? []).map((edge) => {
      const initialEdge = edges.find((e) => e.id === edge.id);
      if (!initialEdge) {
        throw new Error('Invalid layout, edge not found');
      }

      return {
        ...initialEdge,
        source: edge.sources[0],
        target: edge.targets[0],
      };
    }),
  } as ProcessFlowLayout;
}
